import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Search, Download, RefreshCw } from "lucide-react";
import TableContainer from "Common/TableContainer";
import { toast, ToastContainer } from "react-toastify";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import PaginationTest from "Common/Components/Paginationtest";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import axios from "axios";
import { exportToExcel } from "Common/Components/Excel";
import ModalData from "Common/Components/ModalDate/ModalData";
import { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import { useAssistantFilter } from "Common/Assistant/lib/pageFilter";

interface ReportRow {
    row_type: string;
    label: string;
    voucher_bag_count: number;
    driver_discharge_bag_count: number;
    inspector_registration_bag_count: number;
    driver_not_discharged_bag_count: number;
    inspector_not_registered_bag_count: number;
}

interface ReportResponse {
    filters: {
        start_date: string;
        end_date: string;
        voucher_to_discharge_days: number;
        discharge_to_inspection_days: number;
    };
    rows: ReportRow[];
}

const toEnglishDigits = (str: string): string => {
    const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
    const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    const englishDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

    let result = str;
    persianDigits.forEach((d, i) => {
        result = result.replace(new RegExp(d, 'g'), englishDigits[i]);
    });
    arabicDigits.forEach((d, i) => {
        result = result.replace(new RegExp(d, 'g'), englishDigits[i]);
    });
    return result;
};

const ReportsPage: React.FC = () => {
    // State های فیلترها
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");
    const [voucherToDischarge, setVoucherToDischarge] = useState<number>(30);
    const [dischargeToInspection, setDischargeToInspection] = useState<number>(30);

    // State های صفحه‌بندی
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [selectedNumber, setSelectedNumber] = useState<number>(20);
    const [totalPages, setTotalPages] = useState<number>(0);

    // State های داده و وضعیت
    const [reportRows, setReportRows] = useState<ReportRow[]>([]);
    const [allRows, setAllRows] = useState<ReportRow[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");

    const isFirstRun = useRef(true);

    // فیلتری که دستیار سامانه فرستاده را برمی‌دارد.
    // isFirstRun دوباره true می‌شود تا افکت بارگذاری خودکار با
    // تاریخ‌های تازه یک بار دیگر اجرا شود؛ وگرنه صفحه با پیش‌فرض
    // «امروز» می‌ماند و کاربر فکر می‌کند دستیار کاری نکرده.
    useAssistantFilter("reports", (filter) => {
        if (filter.pageSize) setSelectedNumber(filter.pageSize);
        if (filter.voucherToDischargeDays) setVoucherToDischarge(filter.voucherToDischargeDays);
        if (filter.dischargeToInspectionDays)
            setDischargeToInspection(filter.dischargeToInspectionDays);

        if (filter.startDate && filter.endDate) {
            isFirstRun.current = true;
            setStartDate(filter.startDate);
            setEndDate(filter.endDate);
        }
        setCurrentPage(1);
    });

    // مقداردهی اولیه تاریخ‌ها
    useEffect(() => {
        const today = new Date();
        const todayPersian = new DateObject({ date: today, calendar: persian });
        const todayStr = toEnglishDigits(todayPersian.format("YYYY/MM/DD"));
        setStartDate(todayStr);
        setEndDate(todayStr);
    }, []);

    useEffect(() => {
        document.body.classList.remove('overflow-hidden');
        document.body.style.overflow = '';
        const observer = new MutationObserver(() => {
            if (document.body.classList.contains('overflow-hidden')) {
                document.body.classList.remove('overflow-hidden');
                document.body.style.overflow = '';
            }
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    // تابع فراخوانی API
    const fetchReport = useCallback(async () => {
        if (!startDate || !endDate) {
            toast.warning("لطفاً هر دو تاریخ را انتخاب کنید");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const response = await axios.get<ReportResponse>(
                "https://rasad-api.havirard.ir/monitoring/reports/flour-distribution",
                {
                    params: {
                        start_date: startDate,
                        end_date: endDate,
                        voucher_to_discharge_days: voucherToDischarge,
                        discharge_to_inspection_days: dischargeToInspection,
                    },
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("app_havir")}`,
                    },
                }
            );

            const rows = response.data.rows || [];
            console.log("📦 تعداد رکوردهای دریافتی از API:", rows.length);
            setAllRows(rows);
            setCurrentPage(1);

            if (rows.length === 0) {
                toast.info("هیچ داده‌ای برای بازه انتخاب‌شده یافت نشد");
            }
        } catch (err: any) {
            const errorMsg = err.response?.data?.detail || "خطا در دریافت گزارش";
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, voucherToDischarge, dischargeToInspection]);

    // بارگذاری خودکار در اولین رندر
    useEffect(() => {
        if (isFirstRun.current && startDate && endDate) {
            isFirstRun.current = false;
            fetchReport();
        }
    }, [startDate, endDate, fetchReport]);

    // ========== مدیریت خودکار reportRows با useEffect ==========
    useEffect(() => {
        console.log("🔄 allRows یا selectedNumber تغییر کرد");
        console.log("📊 allRows.length:", allRows.length);
        console.log("🔢 selectedNumber:", selectedNumber);

        const startIndex = (currentPage - 1) * selectedNumber;
        const endIndex = Math.min(startIndex + selectedNumber, allRows.length);
        const sliced = allRows.slice(startIndex, endIndex);
        console.log("📋 تعداد ردیف‌های نمایش داده شده:", sliced.length);
        setReportRows(sliced);

        // محاسبه تعداد صفحات
        const pages = Math.ceil(allRows.length / selectedNumber) || 1;
        setTotalPages(pages);
    }, [allRows, selectedNumber, currentPage]);

    // مدیریت تغییر صفحه
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // مدیریت تغییر تعداد سطر
    const handleSizeChange = (value: number) => {
        console.log("🔽 مقدار انتخاب شده:", value);
        setSelectedNumber(value);
        setCurrentPage(1);
    };

    // ستون‌های جدول
    const columns = useMemo(
        () => [
            {
                header: "عنوان ردیف",
                accessorKey: "label",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() || "-",
            },
            {
                header: "حواله نانوا",
                accessorKey: "voucher_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
            {
                header: "تعداد کیسه تخلیه شده   ",
                accessorKey: "driver_discharge_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
            {
                header: "ثبت بازرس",
                accessorKey: "inspector_registration_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
            {
                header: "عدم تخلیه راننده",
                accessorKey: "driver_not_discharged_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
            {
                header: "عدم ثبت بازرس",
                accessorKey: "inspector_not_registered_bag_count",
                enableColumnFilter: false,
                cell: (cell: any) => cell.getValue() ?? 0,
            },
        ],
        []
    );

    // خروجی اکسل
    const handleExportExcel = () => {
        if (allRows.length === 0) {
            toast.warning("داده‌ای برای خروجی وجود ندارد");
            return;
        }
        const exportData = allRows.map((row) => ({
            "عنوان ردیف": row.label,
            "حواله نانوا": row.voucher_bag_count,
            "تخلیه راننده": row.driver_discharge_bag_count,
            "ثبت بازرس": row.inspector_registration_bag_count,
            "عدم تخلیه راننده": row.driver_not_discharged_bag_count,
            "عدم ثبت بازرس": row.inspector_not_registered_bag_count,
        }));
        exportToExcel(exportData, "FlourDistributionReport");
    };

    const handleSearch = () => {
        fetchReport();
    };

    return (
        <React.Fragment>
            <BreadCrumb pageTitle="گزارش‌ها" title="پایش آرد" />
            <ToastContainer position="top-center" closeButton={false} limit={3} rtl />

            <div className="card">
                <div className="card-body">
                    {/* بخش فیلترها */}
                    <div className="flex flex-col gap-4 mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                            <ModalData lableprop="از تاریخ" data={startDate} setData={setStartDate} />
                            <ModalData lableprop="تا تاریخ" data={endDate} setData={setEndDate} />
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-white mb-1">
                                    فاصله زمانی حواله تا تخلیه
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    value={voucherToDischarge}
                                    onChange={(e) =>
                                        setVoucherToDischarge(Number(e.target.value) || 0)
                                    }
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-zinc-700 dark:border-zinc-600 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-white mb-1">
                                    فاصله زمانی تخلیه تا بازرسی
                                </label>
                                <input
                                    type="number"
                                    min={0}
                                    value={dischargeToInspection}
                                    onChange={(e) =>
                                        setDischargeToInspection(Number(e.target.value) || 0)
                                    }
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 dark:bg-zinc-700 dark:border-zinc-600 dark:text-white"
                                />
                            </div>
                        </div>

                        {/* دکمه‌های عملیات */}
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <RefreshCw
                                    className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`}
                                    onClick={handleSearch}
                                />
                                <span className="text-sm font-medium">
                                    مجموع رکوردها:{" "}
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                                        {allRows.length}
                                    </span>
                                </span>
                            </div>
                            <div className="flex flex-wrap gap-3 items-center">
                                <TableDropDownSmall onSelect={handleSizeChange} />
                                <button
                                    onClick={handleSearch}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700"
                                >
                                    <Search size={16} /> جستجو
                                </button>
                                <button
                                    onClick={handleExportExcel}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50"
                                >
                                    <Download size={16} /> اکسل
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* نمایش محتوای اصلی */}
                    {loading ? (
                        <LoadingCounter count={0} text="در حال بارگذاری..." />
                    ) : error ? (
                        <LoadingCounter count={0} text={error} />
                    ) : allRows.length === 0 ? (
                        <div className="flex justify-center mx-auto mt-10 w-full">
                            <button
                                onClick={handleSearch}
                                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-xl hover:bg-green-700"
                            >
                                <Search size={16} /> جستجو با مقادیر پیش فرض
                            </button>
                        </div>
                    ) : (
                        <>
                            <TableContainer
                                isPagination={false}
                                columns={columns}
                                data={reportRows}
                                customPageSize={reportRows.length}  // ← این خط را اضافه کن
                                divclassName="-mx-5 overflow-x-auto"
                                tableclassName="w-full whitespace-nowrap"
                                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
                                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                                PaginationClassName="hidden"
                            />
                            <PaginationTest
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};

export default ReportsPage;